import {
  ChangeEventHandler,
  forwardRef,
  InputHTMLAttributes,
  memo,
  ReactNode,
  Ref,
  useCallback,
  useMemo
} from 'react'
import cn from 'utils/cn'

type InputRadioProps = InputHTMLAttributes<HTMLInputElement>

export const InputRadio = forwardRef(
  (
    { className = '', ...rest }: InputRadioProps,
    ref: Ref<HTMLInputElement>
  ) => {
    return (
      <input
        ref={ref}
        className={cn(
          'appearance-none relative w-4 h-4 rounded-full mr-2 outline-0! bg-white border-dark-2 border disabled:border-dark-2 disabled:bg-dark-1 disabled:border-[2px] disabled:cursor-auto disabled:pointer-events-none checked:border-primary group-hover:border-primary group-hover:checked:shadow-xs group-hover:checked:shadow-primary focus:outline-0! focus:shadow-primary focus:border-2 after:bg-primary after:w-2 after:h-2 after:absolute after:rounded-full after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:invisible checked:after:visible after:disabled:bg-dark-2 read-only:pointer-events-none',
          className
        )}
        type="radio"
        {...rest}
      />
    )
  }
)

type Value = string | number

type OptionData = {
  label: string | number | ReactNode
  value: Value
  disabled?: boolean
}

export interface RadioProps {
  /**
   * Array of radio options (string, number, or OptionData object).
   */
  options: Array<string | number | OptionData>
  /**
   * The currently selected value.
   */
  value?: Value
  /**
   * Name attribute for the radio group.
   */
  name?: string
  /**
   * Callback fired when the selected value changes.
   */
  onChange?: (value: Value) => void
  /**
   * Additional class names for the radio group container.
   */
  className?: string
  /**
   * If true, disables all radio options.
   */
  disabled?: boolean
  /**
   * If true, makes all radio options read-only.
   */
  readOnly?: boolean
  /**
   * Layout orientation for the radio group.
   */
  orientation?: 'vertical' | 'horizontal'
  /**
   * If true, aligns radio buttons to the top of their labels.
   */
  alignTop?: boolean
}

/**
 * Radio component supporting custom options, vertical/horizontal layout, and disabled/read-only states.
 * Integrates with forms and allows custom alignment of radio buttons.
 */
const Radio = memo(
  forwardRef(
    (
      {
        value = '',
        options,
        name = '',
        onChange = console.log,
        className,
        disabled = false,
        readOnly = false,
        orientation = 'vertical',
        alignTop
      }: RadioProps,
      ref: Ref<HTMLDivElement>
    ) => {
      const onChangeCB: ChangeEventHandler<HTMLInputElement> = useCallback(
        (evt) => {
          onChange(evt.target.value)
        },
        [onChange]
      )

      const optionsList = useMemo(
        () =>
          options.map((opt) => {
            const label = typeof opt === 'object' ? opt.label : opt
            const optValue = typeof opt === 'object' ? opt.value : opt
            const optDisabled = typeof opt === 'object' ? opt.disabled : false
            return (
              <div
                className={cn(
                  'group',
                  disabled || readOnly || optDisabled
                    ? 'pointer-events-none'
                    : 'cursor-pointer',
                  className
                )}
                key={'options-' + optValue}
              >
                <label
                  className={cn(
                    'flex',
                    disabled || readOnly || optDisabled
                      ? 'pointer-events-none'
                      : 'cursor-pointer',
                    alignTop ? 'items-start' : 'items-center'
                  )}
                  htmlFor={'options-' + optValue}
                >
                  <InputRadio
                    className={cn(alignTop && 'mt-[5px]')}
                    value={optValue}
                    name={name}
                    onChange={onChangeCB}
                    checked={optValue === value}
                    id={'options-' + optValue}
                    disabled={disabled || optDisabled}
                    readOnly={readOnly}
                  />
                  {label}
                </label>
              </div>
            )
          }),
        [
          alignTop,
          className,
          disabled,
          name,
          onChangeCB,
          options,
          readOnly,
          value
        ]
      )

      return (
        <div
          ref={ref}
          className={cn(
            'flex',
            orientation === 'vertical'
              ? 'flex-col gap-3'
              : 'flex-row gap-6 items-center'
          )}
        >
          {optionsList}
        </div>
      )
    }
  )
)
Radio.displayName = 'Radio'
export default Radio
